import {
  addToCart,
  CartError,
  clearGiftError,
  publishCartUpdate,
  selectedId,
  selectedIsAvailable,
  selectedInput,
  showGiftError,
} from '@theme/cart';
import { withPendingButton } from '@theme/utilities';

/**
 * The inline "Add to bag" button: adds the selected variant and reports the
 * outcome in place.
 *
 * Everything that talks to the cart now lives in `@theme/cart`. This file used
 * to carry its own copies of addVariant, collectProperties, selectedInput,
 * selectedId, selectedIsAvailable, showGiftError and clearGiftError — the same
 * seven functions `product-sticky-buy.js` also carried, almost line for line.
 * What is left here is what is actually about this button: its label, its
 * sold-out state, and its spinner.
 */
(function () {
  if (window.__productBuyInit) return;
  window.__productBuyInit = true;

  // Repurpose the add button for the selected size: "Add to bag" when it can
  // be bought, "Notify me" (opening the back-in-stock drawer) when it's sold
  // out, and a disabled "Sold out" when that drawer isn't there. A dead
  // primary CTA with the real action hiding in a small link below it is the
  // worse trade — and the persistent bar already behaves this way, so this
  // keeps the two surfaces consistent. Runs on selection + at load.
  function updateBuyState(root) {
    const input = selectedInput(root.closest('form'));
    const soldOut = !!input && input.getAttribute('data-variant-available') !== 'true';
    const addText = root.getAttribute('data-add-text') || '';
    const soldOutText = root.getAttribute('data-sold-out-text') || '';
    const notifyText = root.getAttribute('data-notify-text') || '';
    // Rendered by the "Product options" block, and only when its "Show Notify
    // me" setting is on and the Klaviyo key is set — that checkbox is the
    // single switch for the whole back-in-stock feature.
    const drawer = soldOut ? document.querySelector('[data-notify-drawer]') : null;

    const submit = root.querySelector('[data-buy-submit]');
    if (submit) {
      const label = submit.querySelector('[data-buy-label]') || submit;
      label.textContent = soldOut ? (drawer ? notifyText : soldOutText) : addText;

      // Let assets/drawers.js own the open off this same click. Its
      // preventDefault also stops the implicit form submit, and the submit
      // handler below refuses sold-out variants anyway — so there's no path
      // from here into the cart.
      if (drawer) submit.setAttribute('data-drawer-open', drawer.id);
      else submit.removeAttribute('data-drawer-open');

      submit.disabled = soldOut && !drawer;
      if (submit.disabled) submit.setAttribute('aria-disabled', 'true');
      else submit.removeAttribute('aria-disabled');
    }
  }

  function init(root) {
    if (root.__buyInit) return;
    root.__buyInit = true;

    const form = root.closest('form');
    const submit = root.querySelector('[data-buy-submit]');
    const error = root.querySelector('[data-buy-error]');
    const msg = root.getAttribute('data-msg-select-size') || 'Please select a size';

    function fail(text) {
      if (error) { error.hidden = false; error.textContent = text; }
    }
    function clear() {
      if (error) { error.hidden = true; error.textContent = ''; }
      clearGiftError();
    }

    async function doAdd(id, btn) {
      await withPendingButton(
        btn,
        async () => {
          try {
            // `sections` comes back with the add: the drawer is rendered against
            // the cart this request just changed, so the listener needs no
            // round trip.
            const { sections } = await addToCart(id);
            clear();
            publishCartUpdate({ open: true, sections });
          } catch (e) {
            // Only a refusal from Shopify carries copy worth showing: its
            // `description` is localized and written for shoppers. A network
            // failure's message is "Failed to fetch", which belongs in no UI.
            if (e instanceof CartError) showGiftError(e.message);
            fail(e.message || 'Error');
          }
        },
        { onSettled: () => updateBuyState(root) }
      );
    }

    // Inline submit (owns the shared product form).
    if (form && !form.__buySubmit) {
      form.__buySubmit = true;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        const id = selectedId(form);
        if (!id) { fail(msg); return; }
        // Sold-out size selected: never add to cart. When the back-in-stock
        // drawer exists the button carries `data-drawer-open` and drawers.js
        // opens it off the click instead.
        if (!selectedIsAvailable(form)) { return; }
        doAdd(id, submit);
      });
    }

    // Reflect the current selection at load (single-variant, or a checked size).
    updateBuyState(root);
  }

  // Keep the button state in sync with the chosen variant.
  document.addEventListener('variant:change', function (e) {
    const id = e.detail && e.detail.id;
    if (!id) return;
    document.querySelectorAll('[data-product-buy]').forEach(function (root) {
      updateBuyState(root);
    });
  });

  function initAll() {
    document.querySelectorAll('[data-product-buy]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
